import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { ApprovalRow, DecideApprovalArgs } from '../lib/database.types'
import { keys } from './queries'
import type { DecisionState } from './map/approvals'

/**
 * The only write this application makes.
 *
 * It does not UPDATE the table -- it cannot, the grant was removed and the row
 * policy dropped. decide_approval stamps decided_by from the verified JWT,
 * appends the events record in the same transaction, and takes a row lock, so
 * two operators deciding the same item produce one decision and one visible
 * error rather than a silent overwrite.
 *
 * Undo is the same call with 'pending'. It appends an approval.undecided event;
 * the original decision's record stays in the feed, which is what product rule 4
 * means by immutable. The row's current state is not the record.
 */
export function useDecide() {
  const client = useQueryClient()

  return useMutation({
    mutationFn: async (input: { id: string; state: DecisionState; note?: string | null }) => {
      // Typed as the function's own Args, and p_note omitted rather than set to
      // null when absent: postgrest-js matches an rpc overload on the exact
      // argument shape, so a narrowed union or an explicit null fails the match
      // as surely as a missing key would.
      const note = input.note?.trim()
      const args: DecideApprovalArgs = note
        ? { p_id: input.id, p_state: input.state, p_note: note }
        : { p_id: input.id, p_state: input.state }
      const { data, error } = await supabase.rpc('decide_approval', args)
      if (error) throw new Error(friendly(error.message))
      return data as unknown as ApprovalRow
    },

    // Optimistic, because deciding a queue between meetings should not wait on a
    // round trip. The snapshot below is what puts the row back if the server
    // disagrees -- which it will, deliberately, when someone else got there first.
    onMutate: async (input) => {
      await client.cancelQueries({ queryKey: keys.approvals })
      const previous = client.getQueryData<ApprovalRow[]>(keys.approvals)
      client.setQueryData<ApprovalRow[]>(keys.approvals, (rows) =>
        (rows ?? []).map((row) =>
          row.id === input.id
            ? {
                ...row,
                state: input.state,
                decided_by: input.state === 'pending' ? null : 'you',
                decided_at: input.state === 'pending' ? null : new Date().toISOString(),
                decision_note: input.state === 'pending' ? null : (input.note ?? null),
              }
            : row,
        ),
      )
      return { previous }
    },

    onError: (_error, _input, context) => {
      if (context?.previous) client.setQueryData(keys.approvals, context.previous)
    },

    // The decision also wrote an event, so the feed is stale too -- and if the
    // item was one the watchdog filed, so is the fleet line in the cost strip.
    onSettled: () => {
      void client.invalidateQueries({ queryKey: keys.approvals })
      void client.invalidateQueries({ queryKey: keys.fleetHealth })
      void client.invalidateQueries({ queryKey: ['events'] })
    },
  })
}

/**
 * decide_approval raises deliberate, readable errors. Passing raw Postgres text
 * to someone deciding a queue is not useful, but neither is hiding that the
 * reason was another person.
 */
function friendly(message: string): string {
  if (message.includes('already')) {
    return message.replace(/^.*decide_approval: /, '').replace(/^(\w)/, (c) => c.toUpperCase())
  }
  if (message.includes('no authenticated identity')) {
    return 'Your session expired before that saved. Sign in again and the item is still waiting.'
  }
  if (message.includes('Could not find the function')) {
    return 'decide_approval is not applied to this database yet, so nothing was written.'
  }
  return message
}
